import { FC } from 'react'

import { FormGroup, Input, useHelmet } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { PotentialBidEditableFields } from 'modules/domain/bid/types'
import { Product } from 'modules/domain/collection/types'
import { refetchFunc } from 'modules/domain/common/hooks'
import { PotentialBid } from 'modules/domain/potentialBid/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { useTranslation } from 'react-i18next'
import { DatePicker } from 'views/components/DatePicker/DatePicker'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { ShortFormControl } from 'views/styled/common'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import { SeasonSelect } from '../../components/SeasonSelect'
import { InitialMainParameter } from '../BidCreateModal'
import { PotentialBidsParams } from './PotentialBidsParams'

type Props = {
  onClose: () => void
  onSuccess: refetchFunc
  bid: PotentialBid
  product?: Product
}

export const PotentialBidEditModal: FC<Props> = ({ onClose, onSuccess, bid, product }) => {
  const { t } = useTranslation('bid')
  useHelmet({ title: `${t('form.potentialEdit')} - ${product?.title}` })
  const { formProgress, formHandler } = useAFormHandler('editPotentialBid', { id: bid.id })

  const initialValues = {
    quantity: bid.quantity,
    season: bid.season?.id,
    presumable_sell_date: bid?.presumable_sell_date,
    parameters:
      bid.parameters.map((p) => ({
        parameter: p.parameter.id,
        parameter_value_from: p.parameter_value_from,
        parameter_value_to: p.parameter_value_to,
      })) || [],
    mainParameter: InitialMainParameter,
  }

  const formik = useFormik<PotentialBidEditableFields>({
    initialValues,
    enableReinitialize: true,

    onSubmit: formHandler(async () => await apiClient.put(endpoints.potentialBid(bid.id), formik.values), {
      onSuccess: async () => {
        await onSuccess(true)
        onClose()
      },
      onError: (error) => {
        const { errors } = error
        formik.setErrors(errors)
      },
    }),
  })

  return (
    <ASharedModal onClose={onClose} size="medium" id={AnalyticsPlaces.POTENTIAL_EDIT_MODAL}>
      <SharedModalTitle>{t('form.bidsParams')}</SharedModalTitle>
      <SharedModalContent>
        <SharedModalInputs>
          <ShortFormControl>
            <FormGroup label={t('form.generalQuantity')} error={formik.errors.quantity} required>
              <Input {...formik.getFieldProps('quantity')} invalid={!!formik.errors.quantity} type={'number'} />
            </FormGroup>
          </ShortFormControl>
          <SeasonSelect formik={formik} product_id={bid.product.id} />
          <FormGroup label={t('form.presumableSellDate')} error={formik.errors.presumable_sell_date} required>
            <DatePicker
              value={formik.values.presumable_sell_date}
              onChange={(data) => formik.setFieldValue('presumable_sell_date', data['date'])}
              error={!!formik.errors.presumable_sell_date}
              placeholder={t('common:datePlaceholder')}
              allowClear
            />
          </FormGroup>
        </SharedModalInputs>
        <PotentialBidsParams product={product} formik={formik} />
      </SharedModalContent>

      <ADefaultModalFooter
        onClose={onClose}
        confirmButtonText={t('common:save')}
        onConfirm={() => formik.handleSubmit()}
        progress={formProgress}
        disabled={!formik.dirty}
      />
    </ASharedModal>
  )
}
